// apps/web/src/features/russia-requests/components/tabs/RussiaItemPhotoPreviews.tsx
//
// #1548 read-only photo previews for one SIMPLE position in the cargo
// read view. Resolves each saved photo's signed download URL on demand
// (TanStack Query) and renders read-only thumbnails. Mirrors the #1545
// editor thumbnail shape so read + edit stay visually consistent.
//
// @aiSecurity the resolved `downloadUrl` is a bearer-like temporary
//   access URL; it is read straight off the in-memory query cache into an
//   <img src> and never logged or persisted.

import { useTranslation } from 'react-i18next';
import { Icon } from '../../../../ui/index.js';
import { useRussiaRequestItemPhotoDownloadUrl } from '../../queries.js';

// INVARIANT: only issued-shaped keys (`request-item-photo/<uuid>/<uuid>`)
//   round-trip to a signed download URL; anything else stays inert so a
//   malformed key never triggers a backend call.
const ISSUED_PHOTO_KEY_RE = /^request-item-photo\/[0-9a-f-]{36}\/[0-9a-f-]{36}$/i;

export interface RussiaItemPhotoPreviewsProps {
  requestId: string;
  itemId: string;
  photos: readonly string[];
}

/**
 * Read-only thumbnail strip for a single position's photos. A position
 * with no photos renders nothing (and no broken-image slot).
 */
export function RussiaItemPhotoPreviews({
  requestId,
  itemId,
  photos,
}: RussiaItemPhotoPreviewsProps) {
  if (photos.length === 0) return null;
  return (
    <div
      data-testid={`russia-request-drawer-items-photos-${itemId}`}
      style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
    >
      {photos.map((key) => (
        <RussiaItemPhotoThumb key={key} requestId={requestId} itemId={itemId} objectKey={key} />
      ))}
    </div>
  );
}

function RussiaItemPhotoThumb({
  requestId,
  itemId,
  objectKey,
}: {
  requestId: string;
  itemId: string;
  objectKey: string;
}) {
  const { t } = useTranslation('russiaRequests');
  const isIssued = ISSUED_PHOTO_KEY_RE.test(objectKey);
  const query = useRussiaRequestItemPhotoDownloadUrl(requestId, itemId, objectKey, {
    enabled: isIssued,
  });
  const url = query.data?.downloadUrl ?? null;
  const frameStyle = {
    width: 48,
    height: 36,
    borderRadius: 'var(--r-1)',
    overflow: 'hidden' as const,
    border: '1px solid var(--border-2)',
    background: 'var(--bg-2)',
    display: 'inline-flex',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    color: 'var(--fg-4)',
    flexShrink: 0,
  };
  if (!url) {
    // Neutral placeholder while resolving (or on error): no broken-image.
    return (
      <span style={frameStyle} aria-hidden="true">
        <Icon name="image" size={16} />
      </span>
    );
  }
  return (
    <span style={frameStyle}>
      <img
        src={url}
        alt={t('drawer.items.columns.photos')}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </span>
  );
}
